import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { PermissionApiService } from '@app/infrastructure/permission/permission-api.service';
import { PermissionServiceReworked } from '@app/infrastructure/permission/permission.service.reworked';
import { AuthRoutes, IAccountsPermitted, IOperativeUserRole } from '@app/interfaces';
import { Organization } from '@app/interfaces/accounts_list';
import { IPermissionsMatrix, IPermissionsPayload } from '@app/interfaces/permissions.interface';
import { AuthService } from '@app/services/auth/auth.service';
import { OrganizationsService } from '@app/services/organizations.service';
import { UserService } from '@app/services/user/user.service';
import { WrapToasterService } from '@app/services/wrap-toaster-service';
import { ConfigFacadeService } from '@core/services/config/config-facade.service';
import { PermissionApi } from '@core/services/permission/permission.api';
import {
	IApiPermissionResponse,
	IPermission,
	IPermissionService,
} from '@core/services/permission/permission.interface';
import { PermissionState } from '@core/services/permission/permission.state';
import { PartnersEnum } from '@partners/partners.enum';
import { getActiveOrganization } from '@utils/helpers';
import { BehaviorSubject, combineLatest, Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, map, switchMap, take, tap } from 'rxjs/operators';

@Injectable({
	providedIn: 'root',
})
export class PermissionService implements IPermissionService {
	private isLoaded = false;
	// @Todo: refactor
	private readonly isPermissionSetSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	private readonly NOTIFICATION_MANAGEMENT_KEY = 'notification-management';
	private readonly MY_ORGANIZATION_PAYMENT_KEY = 'my-organization__billing-history';

	get isPermissionSet$(): Observable<boolean> {
		return this.isPermissionSetSubject.asObservable();
	}

	set isPermissionSet(value: boolean) {
		this.isPermissionSetSubject.next(value);
	}

	constructor(
		private organizationsService: OrganizationsService,
		private permissionApiService: PermissionApiService,
		private permissionServiceReworked: PermissionServiceReworked,
		private permissionState: PermissionState,
		private toasterService: WrapToasterService,
		private appConfig: ConfigFacadeService,
		private permissionApi: PermissionApi,
		private userService: UserService,
		private auth: AuthService,
		private router: Router,
	) {}

	public isUpdating$(): Observable<boolean> {
		return this.permissionState.isUpdating$();
	}

	public initPermission(): Observable<boolean> {
		if (this.isLoaded) {
			return of(true);
		}
		return this.loadPermission().pipe(map((data) => data.success));
	}

	public getMatrix(): Observable<IPermissionsMatrix> {
		return this.permissionApi.getMatrix().pipe(map((data) => data.permissions));
	}

	public savePermission(payload: IPermissionsPayload): Observable<boolean> {
		// @Todo: add updating state
		return this.permissionApi.update(payload).pipe(
			map((data) => data.success),
			tap(() => this.toasterService.handleSuccessMessage('Permissions has been updated.')),
			catchError((error) => {
				this.toasterService.handleRequestFailed(error);

				return throwError(error);
			}),
		);
	}

	public getPermission$(): Observable<IPermission> {
		return this.permissionState.getPermission$();
	}

	public getPermission(): IPermission {
		return this.permissionState.getPermission();
	}

	public isPermittedWithPresenter(key: string, presenterMode$: Observable<boolean>): Observable<boolean> {
		return combineLatest([this.isPermitted$(key), presenterMode$]).pipe(
			map(([isPermitted, isPresenterModeEnabled]) => isPermitted && !isPresenterModeEnabled),
		);
	}

	public isPermitted$(key: string): Observable<boolean> {
		if (this.isAdmin()) {
			return of(true);
		}

		// Exception for <NOTIFICATION MANAGEMENT> page
		if (key === this.NOTIFICATION_MANAGEMENT_KEY && !this.isRiBuyer()) {
			return of(this.isNotificationManagementPermitted());
		}

		// Exception for <MY ORGANIZATION > PAYMENT> page
		if (key === this.MY_ORGANIZATION_PAYMENT_KEY && this.isPremium()) {
			return this.organizationsService.getOrganizations().pipe(
				switchMap((organizations) => {
					const currentOrganization: Organization = getActiveOrganization(organizations);
					this.userService.updateUserOrganization(currentOrganization);

					if (currentOrganization.reseller_managed) {
						return of(false);
					}
					return this.getPermission$().pipe(map((value) => !value[key]));
				}),
			);
		}

		return this.getPermission$().pipe(map((value) => !value[key]));
	}

	public isPermitted(key: string): boolean {
		if (!key) {
			return true;
		}

		if (this.isAdmin()) {
			return true;
		}

		// Exception for <NOTIFICATION MANAGEMENT> page
		if (key === this.NOTIFICATION_MANAGEMENT_KEY && !this.isRiBuyer()) {
			return this.isNotificationManagementPermitted();
		}

		// Exception for <MY ORGANIZATION > PAYMENT> page
		if (this.isMyOrganizationPaymentDenied(key, this.userService.organization)) {
			return false;
		}

		const permission = this.getPermission();

		return !permission[key];
	}

	public getPermittedAccounts(permission: string): Observable<IAccountsPermitted> {
		return this.permissionApi.getPermittedAccounts(permission);
	}

	private loadPermission(): Observable<IApiPermissionResponse> {
		this.permissionState.setUpdating(true);

		return this.permissionApiService.loadPermissions().pipe(
			switchMap((isOk) =>
				this.permissionServiceReworked.permissions$.pipe(
					take(1),
					tap((permissions) => {
						window.zestyHost.permissions = permissions;
						const oldPermissions = permissions.reduce((accum, permission) => ({ ...accum, [permission]: true }), {});
						this.permissionState.setPermission(oldPermissions);
						this.isLoaded = true;
						this.permissionState.setUpdating(false);
					}),
					map(() => ({ success: true, permissions: {} })),
				),
			),
			catchError((error) => {
				if (error.status === 401) {
					throw new Error(error);
				}
				return this.userService.user$.pipe(
					concatMap((user) => {
						if (!user || !user.email) {
							throw new Error(error);
						}
						return this.auth.isUserReady(user.email).pipe(
							map((isUserReady) => {
								return {
									email: user.email,
									isUserReady,
								};
							}),
						);
					}),
					concatMap((isUserReadyResponse) => {
						if (!isUserReadyResponse.isUserReady) {
							throw new Error(error);
						}
						return this.router.navigate([AuthRoutes.handleUserProfile, isUserReadyResponse.email]);
					}),
					map(() => {
						throw new Error(error);
					}),
				);
			}),
		);
	}

	private isNotificationManagementPermitted(): boolean {
		return this.appConfig.partnerName === PartnersEnum.zesty || this.isReseller();
	}

	private isMyOrganizationPaymentDenied(key: string, organization: Organization): boolean {
		return key === this.MY_ORGANIZATION_PAYMENT_KEY && this.isPremium() && !!organization.reseller_managed;
	}

	private isAdmin(): boolean {
		return this.userService.userRole === IOperativeUserRole.admin;
	}

	private isReseller(): boolean {
		return this.userService.userRole === IOperativeUserRole.reseller;
	}

	private isRiBuyer(): boolean {
		return this.userService.userRole === IOperativeUserRole.riBuyers;
	}

	private isPremium(): boolean {
		return this.userService.userRole === IOperativeUserRole.premium;
	}
}
